import React from "react";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Add, Remove } from "@mui/icons-material";
import { useCartSectionStyles } from "./CartSection.styles";
import { useI18n } from "../../../../i8n/useI18n";
import { useCart } from "../../../../hooks/useCart";
import { formatMoney } from "../../../../utils/formatPrice";

const CartSection = () => {
    const { classes } = useCartSectionStyles();
    const { t } = useI18n();
    const { cart, loading, isMutating, updateItem, removeItem, goToCheckout } = useCart();

    if (loading) {
        return null;
    }

    if (!cart || cart.lines.length === 0) {
        return (
            <Box className={classes.root}>
                <Box className={classes.container}>
                    <Typography variant="h2" className={classes.title}>{t("cartPage.title")}</Typography>
                    <Box className={classes.empty}>
                        <Typography variant="body1" sx={{ mb: 3 }}>{t("cartPage.empty")}</Typography>
                        <Button variant="contained" component={RouterLink} to={t("links.shop")}>
                            {t("cartPage.continueShopping")}
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.container}>
                <Typography variant="h2" className={classes.title}>{t("cartPage.title")}</Typography>

                <Box className={classes.lines}>
                    {cart.lines.map((line) => (
                        <Box key={line.id} className={classes.line}>
                            <Box className={classes.imageWrapper}>
                                {line.merchandise.image && (
                                    <Box component="img" src={line.merchandise.image.url} alt={line.merchandise.product.title} className={classes.image} />
                                )}
                            </Box>
                            <Box>
                                <Typography component={RouterLink} to={`/produkt/${line.merchandise.product.handle}`} className={classes.productTitle}>
                                    {line.merchandise.product.title}
                                </Typography>
                                <Typography className={classes.variantTitle}>{line.merchandise.title}</Typography>
                                <Box className={classes.quantityControl} sx={{ mt: 1.5 }}>
                                    <IconButton
                                        className={classes.quantityButton}
                                        size="small"
                                        disabled={isMutating}
                                        onClick={() => updateItem(line.id, line.quantity - 1)}
                                    >
                                        <Remove fontSize="small" />
                                    </IconButton>
                                    <Typography className={classes.quantityValue}>{line.quantity}</Typography>
                                    <IconButton
                                        className={classes.quantityButton}
                                        size="small"
                                        disabled={isMutating}
                                        onClick={() => updateItem(line.id, line.quantity + 1)}
                                    >
                                        <Add fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                            <Box className={classes.lineEnd}>
                                <Typography className={classes.lineTotal}>{formatMoney(line.cost.totalAmount)}</Typography>
                                <Button
                                    className={classes.removeButton}
                                    disabled={isMutating}
                                    onClick={() => removeItem(line.id)}
                                >
                                    {t("cartPage.remove")}
                                </Button>
                            </Box>
                        </Box>
                    ))}
                </Box>

                <Box className={classes.summary}>
                    <Box className={classes.summaryRow}>
                        <Typography>{t("cartPage.subtotal")}</Typography>
                        <Typography className={classes.subtotalValue}>{formatMoney(cart.cost.subtotalAmount)}</Typography>
                    </Box>
                    <Typography className={classes.shippingNote}>{t("cartPage.shippingNote")}</Typography>

                    <Box className={classes.checkoutActions}>
                        <Button variant="contained" size="large" disabled={isMutating} onClick={goToCheckout}>
                            {t("cartPage.checkoutCta")}
                        </Button>
                        <Typography className={classes.redirectNote}>{t("cartPage.checkoutRedirectNote")}</Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default CartSection;
